/**
 * Cohort layer module.
 *
 * Renders cohort boundaries as colored polygons.
 */

import type { Map } from 'mapbox-gl';
import type { MapCohort } from '../types/mapTypes';
import { cohortToFeature, featureCollection } from '../utils/geojson';

const SOURCE_ID = 'cohorts-source';
const FILL_LAYER_ID = 'cohorts-fill';
const LINE_LAYER_ID = 'cohorts-line';
const LABEL_LAYER_ID = 'cohorts-label';

const DEFAULT_COHORT_COLORS = [
  '#2563EB', '#7C3AED', '#0891B2', '#059669', '#D97706',
];

export function addCohortLayers(map: Map): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: featureCollection([]),
  });

  map.addLayer({
    id: FILL_LAYER_ID,
    type: 'fill',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'fill-color': ['get', 'boundaryColor'],
      'fill-opacity': 0.1,
    },
    filter: ['==', ['get', '_type'], 'cohort'],
  });

  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'line-color': ['get', 'boundaryColor'],
      'line-width': 2.5,
      'line-opacity': 0.8,
    },
    filter: ['==', ['get', '_type'], 'cohort'],
  });

  map.addLayer({
    id: LABEL_LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 12,
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
    },
    paint: {
      'text-color': ['get', 'boundaryColor'],
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 1.5,
    },
    filter: ['==', ['get', '_type'], 'cohort'],
  });
}

export function updateCohortData(map: Map, cohorts: MapCohort[]): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  const features = cohorts.map((cohort, i) => {
    const feature = cohortToFeature(cohort);
    if (!cohort.boundaryColor) {
      (feature.properties as any).boundaryColor =
        DEFAULT_COHORT_COLORS[i % DEFAULT_COHORT_COLORS.length];
    }
    return feature;
  });

  (source as any).setData(featureCollection(features));
}

export function removeCohortLayers(map: Map): void {
  if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
  if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export { SOURCE_ID as cohortSourceId };
